import type { ReactElement } from "react";
import { Plus, Search } from "lucide-react";
import {
  CONTAINMENT_ACTION_STATUSES,
  CONTAINMENT_EFFECTIVENESS,
  CONTAINMENT_PRIORITIES,
  type ContainmentActionStatus,
  type ContainmentEffectiveness,
  type ContainmentPlanAction,
  type ContainmentPriority,
} from "@playbookai/widget-schemas";
import { Button } from "../../atoms/Button/Button.js";
import { Input } from "../../atoms/Input/Input.js";
import { Select } from "../../molecules/Select/Select.js";
import { cn } from "../../lib/utils.js";
import { ContainmentActionSummary } from "./ContainmentActionSummary.js";

export interface ActionFilters {
  readonly search: string;
  readonly status: ContainmentActionStatus | "all";
  readonly effectiveness: ContainmentEffectiveness | "all";
  readonly priority: ContainmentPriority | "all";
}

/**
 * Master column of the expanded editor. Search, filters and selection are
 * presentation state owned by the parent — none of it is persisted.
 */
export function ActionList({
  actions,
  total,
  filters,
  selectedId,
  readOnly,
  canAdd,
  onFiltersChange,
  onSelect,
  onAdd,
}: {
  actions: readonly ContainmentPlanAction[];
  total: number;
  filters: ActionFilters;
  selectedId: string | undefined;
  readOnly: boolean;
  canAdd: boolean;
  onFiltersChange: (next: ActionFilters) => void;
  onSelect: (actionId: string) => void;
  onAdd: () => void;
}): ReactElement {
  return (
    <section className="flex min-h-0 min-w-0 flex-col rounded-lg border border-pb-border bg-pb-card-elevated/30">
      <header className="flex flex-col gap-2.5 border-b border-pb-border p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-pb-text-secondary">
            Containment actions ({total})
          </h3>
          <Button
            variant="secondary"
            size="sm"
            disabled={readOnly || !canAdd}
            onClick={onAdd}
          >
            <Plus className="h-4 w-4" />
            Add action
          </Button>
        </div>
        <Input
          aria-label="Search actions"
          placeholder="Search actions"
          value={filters.search}
          prefix={<Search className="h-4 w-4 text-pb-text-muted" />}
          onChange={(event) =>
            onFiltersChange({ ...filters, search: event.target.value })
          }
        />
        <details>
          <summary className="cursor-pointer text-xs text-pb-text-secondary">
            Filters
            {[filters.status, filters.effectiveness, filters.priority].some(
              (value) => value !== "all",
            )
              ? " · Active"
              : ""}
          </summary>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Select
              label="Filter: Status"
              value={filters.status}
              options={[
                { value: "all", label: "All statuses" },
                ...CONTAINMENT_ACTION_STATUSES.map((value) => ({
                  value,
                  label: value,
                })),
              ]}
              onChange={(value) =>
                onFiltersChange({
                  ...filters,
                  status: value as ContainmentActionStatus | "all",
                })
              }
            />
            <Select
              label="Filter: Effectiveness"
              value={filters.effectiveness}
              options={[
                { value: "all", label: "All effectiveness" },
                ...CONTAINMENT_EFFECTIVENESS.map((value) => ({
                  value,
                  label: value,
                })),
              ]}
              onChange={(value) =>
                onFiltersChange({
                  ...filters,
                  effectiveness: value as ContainmentEffectiveness | "all",
                })
              }
            />
            <Select
              label="Filter: Priority"
              value={filters.priority}
              options={[
                { value: "all", label: "All priorities" },
                ...CONTAINMENT_PRIORITIES.map((value) => ({
                  value,
                  label: value,
                })),
              ]}
              onChange={(value) =>
                onFiltersChange({
                  ...filters,
                  priority: value as ContainmentPriority | "all",
                })
              }
            />
          </div>
        </details>
      </header>
      {actions.length === 0 ? (
        <p className="px-3 py-8 text-center text-xs italic text-pb-text-muted">
          {total === 0
            ? "No containment actions yet. Add one for each escape point in the flow."
            : "No containment actions match this search and filter combination."}
        </p>
      ) : (
        <ul
          aria-label="Containment actions"
          className="min-h-0 max-h-120 flex-1 divide-y divide-pb-border overflow-y-auto"
        >
          {actions.map((action) => (
            <li key={action.id} className="min-w-0">
              <ActionRowButton
                action={action}
                selected={action.id === selectedId}
                onSelect={() => onSelect(action.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ActionRowButton({
  action,
  selected,
  onSelect,
}: {
  action: ContainmentPlanAction;
  selected: boolean;
  onSelect: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
      className={cn(
        "block w-full min-w-0 border-s-2 border-transparent px-3 py-3 text-start hover:pb-overlay-paint",
        selected && "border-s-2 border-pb-teal bg-pb-teal/10",
      )}
    >
      <ContainmentActionSummary action={action} />
    </button>
  );
}
